//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

// Platform-wide Billing 2.0 credit consumption per organization.
//
// The aggregation is done server-side (usaged GetAllOrganizationsCreditUsage, forwarded
// and enriched with organization/deployment details by idashboard), so the browser never
// sums raw usage items. It counts the same items as the public GetCreditUsage: Billing 2.0
// kinds only, bucketed by the UTC start day of the item, excluding items on non-billable
// tiers, and including items that are not invoiced (or not even priced) yet.

import styled from "@emotion/styled";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DateRangePicker, FocusedInputShape } from "react-dates";
import { Button, Checkbox, Dimmer, Dropdown, Icon, Input, Label, Loader, Menu, Message, Popup, Table } from "semantic-ui-react";
import api from "../../api/api";
import {
  CreditUsageOverview as ApiCreditUsageOverview,
  CreditUsageOverviewItem as ApiCreditUsageOverviewItem,
  GetAllOrganizationsCreditUsageRequest as ApiGetAllOrganizationsCreditUsageRequest,
} from "../../api/lib";
import { ContentSegment, SecondaryMenu, TextLink } from "../../ui/lib";
import { Routes } from "../../routes";

// Billing 2.0 usage item kinds grouped into the columns shown in the table.
// A kind that is not listed here (e.g. a kind added later) is counted under "Other".
const COMPONENTS = [
  { key: "cpu", name: "CPU", kinds: ["CPUHour"], color: "#3f8f2e" },
  { key: "memory", name: "Memory", kinds: ["MemoryHour"], color: "#2f6fa3" },
  { key: "storage", name: "Storage", kinds: ["StorageHour", "StoragePerformanceHour"], color: "#b3760a" },
  { key: "network", name: "Network", kinds: ["NetworkUsageSize"], color: "#7a4fa8" },
  { key: "backup", name: "Backup", kinds: ["CloudStorageHour"], color: "#c2563a" },
  { key: "other", name: "Other", kinds: ["GPUHour", "AddonHour", "AuditLogRequests", "AuditLogData", "DeploymentAEU"], color: "#8b958f" },
] as const;

type ComponentKey = typeof COMPONENTS[number]["key"];
type Credits = Record<ComponentKey, number>;

const componentOfKind = (kind: string): ComponentKey => {
  const comp = COMPONENTS.find((c) => (c.kinds as readonly string[]).includes(kind));
  return comp ? comp.key : "other";
};

// The longest period the server reports on (366 days, see usaged).
const MAX_RANGE_DAYS = 366;

interface IUsageRow {
  organizationId: string;
  deploymentId: string;
  organizationName: string;
  organizationIsDeleted: boolean;
  tier: string;
  deploymentName: string;
  deploymentIsDeleted: boolean;
  projectId: string;
  model: string;
  region: string;
  deploymentCount: number;
  credits: Credits;
  total: number;
}

interface IOrgUsage extends IUsageRow {
  previousTotal: number;
}

interface IDeploymentsState {
  loading: boolean;
  error?: string;
  items: IUsageRow[];
}

const emptyCredits = (): Credits => ({ cpu: 0, memory: 0, storage: 0, network: 0, backup: 0, other: 0 });
const addCredits = (a: Credits, b: Credits): Credits => {
  const r = emptyCredits();
  COMPONENTS.forEach((comp) => (r[comp.key] = a[comp.key] + b[comp.key]));
  return r;
};

// Exported for testing.
export const toUsageRow = (item: ApiCreditUsageOverviewItem): IUsageRow => {
  const usage = item.usage || {};
  const credits = emptyCredits();
  (usage.components || []).forEach((c) => {
    credits[componentOfKind(c.component || "")] += c.credits || 0;
  });
  return {
    organizationId: usage.organization_id || "",
    deploymentId: usage.deployment_id || "",
    organizationName: item.organization_name || "",
    organizationIsDeleted: !!item.organization_is_deleted,
    tier: item.tier_name || item.tier_id || "",
    deploymentName: item.deployment_name || "",
    deploymentIsDeleted: !!item.deployment_is_deleted,
    projectId: item.project_id || "",
    model: item.deployment_model || "",
    region: item.region_id || "",
    deploymentCount: usage.deployment_count || 0,
    credits,
    total: usage.total_credits || 0,
  };
};

// Combines the organizations of the current and the previous period. An organization
// with usage only in the previous period is kept as a row without current credits:
// dropping it would remove its credits from the comparison baseline and hide a decrease.
// Exported for testing.
export const mergePeriods = (current: ApiCreditUsageOverview, previous: ApiCreditUsageOverview): IOrgUsage[] => {
  const previousRows = new Map<string, IUsageRow>();
  (previous.items || []).forEach((item) => {
    const row = toUsageRow(item);
    previousRows.set(row.organizationId, row);
  });
  const rows: IOrgUsage[] = (current.items || []).map((item) => {
    const row = toUsageRow(item);
    const prev = previousRows.get(row.organizationId);
    previousRows.delete(row.organizationId);
    return { ...row, previousTotal: prev ? prev.total : 0 };
  });
  previousRows.forEach((prev) => {
    rows.push({ ...prev, deploymentCount: 0, credits: emptyCredits(), total: 0, previousTotal: prev.total });
  });
  return rows;
};

// The selected calendar days, as the UTC period the server reports on: usage items are
// daily UTC items, so this is what lines up with invoices. `to` is exclusive.
const toUTCPeriod = (from: moment.Moment, to: moment.Moment) => ({
  from: moment.utc(from.format("YYYY-MM-DD")).toDate(),
  to: moment.utc(to.format("YYYY-MM-DD")).add(1, "day").toDate(),
});

// Same endpoint as idashboardClient.GetAllOrganizationsCreditUsage, but
// abortable: the generated client cannot pass an AbortSignal, and a platform-wide
// aggregation is expensive enough that an obsolete one should not keep running.
const creditUsageURL = "/internal-api/dashboard/v1/credit-usage";

const fetchCreditUsage = (from: moment.Moment, to: moment.Moment, organizationId?: string, signal?: AbortSignal): Promise<ApiCreditUsageOverview> => {
  const req: ApiGetAllOrganizationsCreditUsageRequest = {
    ...toUTCPeriod(from, to),
    organization_id: organizationId,
    group_by_deployment: !!organizationId,
  };
  return api.post(creditUsageURL, req, signal);
};

const errorText = (err: any): string => (err && err.message) || String(err);

interface IRangePreset {
  key: string;
  text: string;
  from: moment.Moment;
  to: moment.Moment;
}

// The calendar day of the given moment, in the moment's own mode (local or UTC).
const calendarDay = (m: moment.Moment) => m.format("YYYY-MM-DD");
// Today as a UTC calendar day. Usage is reported per UTC day, so "today" (and with it
// the last complete month, and which days are in the future) follows UTC, not the
// browser's time zone.
const utcToday = () => calendarDay(moment.utc());

// Quick picks next to the calendar. Picking dates by hand switches the dropdown to "Custom range".
// They are built on the UTC calendar (see utcToday). Exported for testing.
export const rangePresets = (now: moment.Moment = moment.utc()): IRangePreset[] => {
  const lastMonth = now.clone().subtract(1, "month");
  const lastQuarter = now.clone().subtract(1, "quarter");
  return [
    { key: "last-month", text: `Last month (${lastMonth.format("MMM YYYY")})`, from: lastMonth.clone().startOf("month"), to: lastMonth.clone().endOf("month") },
    { key: "this-month", text: `This month (${now.format("MMM YYYY")}, in progress)`, from: now.clone().startOf("month"), to: now.clone().endOf("day") },
    { key: "last-30", text: "Last 30 days", from: now.clone().subtract(29, "days").startOf("day"), to: now.clone().endOf("day") },
    {
      key: "last-quarter",
      text: `Last quarter (Q${lastQuarter.quarter()} ${lastQuarter.year()})`,
      from: lastQuarter.clone().startOf("quarter"),
      to: lastQuarter.clone().endOf("quarter"),
    },
    { key: "last-3-months", text: "Last 3 full months", from: now.clone().subtract(3, "months").startOf("month"), to: lastMonth.clone().endOf("month") },
    { key: "ytd", text: `Year to date (${now.year()})`, from: now.clone().startOf("year"), to: now.clone().endOf("day") },
    {
      key: "last-year",
      text: `Last year (${now.year() - 1})`,
      from: now.clone().subtract(1, "year").startOf("year"),
      to: now.clone().subtract(1, "year").endOf("year"),
    },
  ];
};

const formatRange = (from: moment.Moment, to: moment.Moment) =>
  from.isSame(from.clone().startOf("month"), "day") && to.isSame(from.clone().endOf("month"), "day")
    ? from.format("MMMM YYYY")
    : `${from.format("D MMM YYYY")} – ${to.format("D MMM YYYY")}`;

// Credits with 2 decimals, as the other credit views show them: Billing 2.0 credits
// have fractions, so rounding to whole credits would show e.g. 0.4 as 0.
// Exported for testing.
export const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;
const deltaPctOf = (total: number, previousTotal: number): number | undefined => (previousTotal ? ((total - previousTotal) / previousTotal) * 100 : undefined);

// Exported for testing.
// Text starting with =, +, -, @, tab or carriage return is read as a formula by
// spreadsheet software, so an organization named e.g. "=HYPERLINK(...)" would run
// when the export is opened. Such text gets a leading quote (shown as text, not
// evaluated). Numbers, including negative ones like "-12.50", are left as they are.
export const csvField = (value: string | number): string => {
  let s = String(value);
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(s) && !/^[-+]?\d+(\.\d+)?$/.test(s)) {
    s = `'${s}`;
  }
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCSV = (filename: string, rows: (string | number)[][]) => {
  const csv = rows.map((r) => r.map(csvField).join(",")).join("\n") + "\n";
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

type SortColumn = "name" | "tier" | "deployments" | "total" | "delta" | ComponentKey;

// Same visual language as the Billing 2.0 invoice StatsCard (EverestInvoiceList).
const SummaryCard = styled.div`
  --border: #e5e9e5;
  --ink: #182220;
  --ink-2: #56635d;
  --ink-muted: #8b958f;
  --accent: #3f8f2e;
  --good: #2f8f3f;
  --critical: #c23b32;

  background: #fff;
  border: 1px solid var(--border);
  border-radius: 11px;
  box-shadow: 0 1px 2px rgba(20, 30, 25, 0.04), 0 6px 18px rgba(20, 30, 25, 0.05);
  margin-bottom: 14px;
  display: grid;
  grid-template-columns: auto 1fr;

  .cell {
    padding: 10px 16px;
    min-width: 0;
  }
  .cell.left {
    border-right: 1px solid var(--border);
  }
  .title {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--ink-muted);
    margin-bottom: 8px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }
  .title .ctx {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
  }
  .kpis {
    display: flex;
    flex-wrap: wrap;
  }
  .kpi {
    padding: 2px 16px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    position: relative;
  }
  .kpi:first-child {
    padding-left: 0;
  }
  .kpi + .kpi::before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    bottom: 4px;
    width: 1px;
    background: var(--border);
  }
  .kpi__value {
    font-size: 22px;
    line-height: 1;
    font-weight: 650;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  .kpi__label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ink-muted);
    white-space: nowrap;
  }
  .kpi--total .kpi__label {
    color: var(--accent);
  }
  .up {
    color: var(--critical);
  }
  .down {
    color: var(--good);
  }
  .bar {
    display: flex;
    height: 14px;
    border-radius: 4px;
    overflow: hidden;
    gap: 2px;
    margin-bottom: 8px;
  }
  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 18px;
    font-size: 12px;
    color: var(--ink-2);
  }
  .legend .sw {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 2px;
    margin-right: 6px;
  }
  .legend b {
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    .cell.left {
      border-right: none;
      border-bottom: 1px solid var(--border);
    }
  }
`;

const NumCell = styled(Table.Cell)`
  text-align: right !important;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const ShareBar = styled.div`
  display: inline-block;
  vertical-align: middle;
  width: 48px;
  height: 6px;
  margin-left: 6px;
  border-radius: 3px;
  background: #e5e9e5;
  overflow: hidden;
  > div {
    height: 100%;
    background: #3f8f2e;
  }
`;

const DeploymentRow = styled(Table.Row)`
  &&& td {
    background: #f6f8f6;
    font-size: 0.92em;
  }
`;

const UsageOverview = () => {
  const presets = useMemo(() => rangePresets(), []);
  // Default to the last complete month - that's what lines up with invoices.
  const [presetKey, setPresetKey] = useState(presets[0].key);
  // The period shown. Only changes once both ends are picked.
  const [period, setPeriod] = useState({ from: presets[0].from, to: presets[0].to });
  // What the date picker shows, including a half-picked range.
  const [pickerStart, setPickerStart] = useState<moment.Moment | null>(presets[0].from);
  const [pickerEnd, setPickerEnd] = useState<moment.Moment | null>(presets[0].to);
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("all");
  const [hideZero, setHideZero] = useState(true);
  const [sortColumn, setSortColumn] = useState<SortColumn>("total");
  const [sortAsc, setSortAsc] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [allOrgs, setAllOrgs] = useState<IOrgUsage[]>([]);
  // Per-deployment usage of an organization, fetched when its row is expanded.
  const [deployments, setDeployments] = useState<Record<string, IDeploymentsState>>({});

  const { from, to } = period;
  // Lets a per-deployment fetch tell that the period changed while it was in flight.
  const periodRef = useRef(period);
  periodRef.current = period;
  const rangeDays = to.diff(from, "days") + 1;
  // The "previous period" is the same number of days immediately before `from`.
  const prevTo = from.clone().subtract(1, "day").endOf("day");
  const prevFrom = from.clone().subtract(rangeDays, "days").startOf("day");
  const rangeTooLong = rangeDays > MAX_RANGE_DAYS;

  useEffect(() => {
    setExpanded(new Set());
    setDeployments({});
    if (rangeTooLong) {
      // The cleanup of the previous run cancelled any fetch in flight, so its
      // loading state must be cleared here or the page stays dimmed.
      setAllOrgs([]);
      setLoading(false);
      setError(undefined);
      return;
    }
    let cancelled = false;
    // Aborted on cleanup (period change, leaving the page), so obsolete aggregations stop.
    const abort = new AbortController();
    setLoading(true);
    setError(undefined);
    Promise.all([fetchCreditUsage(from, to, undefined, abort.signal), fetchCreditUsage(prevFrom, prevTo, undefined, abort.signal)])
      .then(([current, previous]) => {
        if (cancelled) {
          return;
        }
        setAllOrgs(mergePeriods(current, previous));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setAllOrgs([]);
        setError(errorText(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
      abort.abort();
    };
    // prevFrom/prevTo are derived from from/to.
  }, [from, to, rangeTooLong]);

  const onPresetChanged = (key: string) => {
    const p = presets.find((x) => x.key === key);
    if (p) {
      setPresetKey(key);
      setPickerStart(p.from);
      setPickerEnd(p.to);
      setPeriod({ from: p.from, to: p.to });
    }
  };
  const onDatesChanged = (args: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => {
    // Keep the half-picked start visible while the user still has to click an end date.
    setPickerStart(args.startDate);
    setPickerEnd(args.endDate);
    if (args.startDate && args.endDate) {
      const start = args.startDate.clone().startOf("day");
      const end = args.endDate.clone().endOf("day");
      setPeriod({ from: start, to: end });
      const match = presets.find((p) => calendarDay(p.from) === calendarDay(start) && calendarDay(p.to) === calendarDay(end));
      setPresetKey(match ? match.key : "custom");
    }
  };

  const tierOptions = useMemo(() => _.sortBy(_.uniq(allOrgs.map((o) => o.tier).filter((t) => !!t))), [allOrgs]);

  const orgs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allOrgs.filter(
      (o) =>
        (tier === "all" || o.tier === tier) &&
        (!q || o.organizationName.toLowerCase().includes(q) || o.organizationId.toLowerCase().includes(q)) &&
        // Organizations whose credits dropped to zero stay visible: they are part of the comparison.
        (!hideZero || o.total > 0 || o.previousTotal > 0)
    );
    const key = (o: IOrgUsage): string | number => {
      switch (sortColumn) {
        case "name":
          return (o.organizationName || o.organizationId).toLowerCase();
        case "tier":
          return o.tier;
        case "deployments":
          return o.deploymentCount;
        case "total":
          return o.total;
        case "delta": {
          // Sort on the percentage the column shows. An organization without
          // usage in the previous period ("new") sorts as the largest increase.
          const delta = deltaPctOf(o.total, o.previousTotal);
          return delta === undefined ? (o.total > 0 ? Number.POSITIVE_INFINITY : 0) : delta;
        }
        default:
          return o.credits[sortColumn];
      }
    };
    return _.orderBy(filtered, [key], [sortAsc ? "asc" : "desc"]);
  }, [allOrgs, search, tier, hideZero, sortColumn, sortAsc]);

  const totals = orgs.reduce((acc, o) => addCredits(acc, o.credits), emptyCredits());
  const grandTotal = _.sumBy(orgs, (o) => o.total);
  const previousGrandTotal = _.sumBy(orgs, (o) => o.previousTotal);
  const deltaPct = deltaPctOf(grandTotal, previousGrandTotal);
  const deploymentCount = _.sumBy(orgs, (o) => o.deploymentCount);
  const inProgress = calendarDay(to) >= utcToday();

  const onSort = (col: SortColumn) => {
    if (col === sortColumn) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(col);
      setSortAsc(col === "name" || col === "tier");
    }
  };
  const sorted = (col: SortColumn): "ascending" | "descending" | undefined => (sortColumn === col ? (sortAsc ? "ascending" : "descending") : undefined);
  // Props of a sortable header cell: sorts on click and, for keyboard users, on Enter/Space.
  const sortProps = (col: SortColumn) => ({
    sorted: sorted(col),
    "aria-sort": sorted(col) || "none",
    tabIndex: 0,
    onClick: () => onSort(col),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSort(col);
      }
    },
  });

  const loadDeployments = (orgId: string) => {
    setDeployments((prev) => ({ ...prev, [orgId]: { loading: true, items: [] } }));
    const requested = period;
    fetchCreditUsage(from, to, orgId)
      .then((result) => {
        if (periodRef.current !== requested) {
          return;
        }
        setDeployments((prev) => (prev[orgId] ? { ...prev, [orgId]: { loading: false, items: (result.items || []).map(toUsageRow) } } : prev));
      })
      .catch((err) => {
        if (periodRef.current !== requested) {
          return;
        }
        setDeployments((prev) => (prev[orgId] ? { ...prev, [orgId]: { loading: false, error: errorText(err), items: [] } } : prev));
      });
  };
  const toggle = (orgId: string) => {
    const next = new Set(expanded);
    if (next.has(orgId)) {
      next.delete(orgId);
    } else {
      next.add(orgId);
      const state = deployments[orgId];
      if (!state || state.error) {
        loadDeployments(orgId);
      }
    }
    setExpanded(next);
  };

  const onExportCSV = () => {
    const header = [
      "Organization ID",
      "Organization",
      "Tier",
      "Deployments",
      ...COMPONENTS.map((c) => `${c.name} credits`),
      "Total credits",
      "Share %",
      "Previous period credits",
      "Change %",
    ];
    const rows = orgs.map((o) => {
      const delta = deltaPctOf(o.total, o.previousTotal);
      return [
        o.organizationId,
        o.organizationName,
        o.tier,
        o.deploymentCount,
        ...COMPONENTS.map((c) => o.credits[c.key].toFixed(2)),
        o.total.toFixed(2),
        grandTotal ? ((o.total / grandTotal) * 100).toFixed(2) : "0.00",
        o.previousTotal.toFixed(2),
        delta === undefined ? "" : delta.toFixed(2),
      ];
    });
    downloadCSV(`usage_${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}.csv`, [header, ...rows]);
  };

  const deltaCell = (total: number, previousTotal: number) => {
    const delta = deltaPctOf(total, previousTotal);
    if (delta === undefined) {
      return <NumCell>{total ? "new" : "-"}</NumCell>;
    }
    return (
      <NumCell negative={delta > 25} positive={delta < -25}>
        {fmtPct(delta)}
      </NumCell>
    );
  };

  const deploymentRows = (o: IOrgUsage) => {
    const state = deployments[o.organizationId];
    if (!state || state.loading) {
      return (
        <DeploymentRow>
          <Table.Cell />
          <Table.Cell colSpan={COMPONENTS.length + 6}>
            <Loader active inline size="mini" /> Loading deployments&hellip;
          </Table.Cell>
        </DeploymentRow>
      );
    }
    if (state.error) {
      return (
        <DeploymentRow>
          <Table.Cell />
          <Table.Cell colSpan={COMPONENTS.length + 6}>
            <Icon name="warning sign" color="red" />
            Failed to load deployments: {state.error}
          </Table.Cell>
        </DeploymentRow>
      );
    }
    if (state.items.length === 0) {
      // An organization that only had usage in the previous period.
      return (
        <DeploymentRow>
          <Table.Cell />
          <Table.Cell colSpan={COMPONENTS.length + 6}>No usage in this period.</Table.Cell>
        </DeploymentRow>
      );
    }
    return state.items.map((d) => (
      <DeploymentRow key={d.deploymentId || "-"}>
        <Table.Cell />
        <Table.Cell colSpan={2}>
          <Icon name="database" color="grey" />
          {d.deploymentId ? (
            <TextLink href={Routes.dashboard_support_deployment_detailsWithId(d.deploymentId)}>{d.deploymentName || d.deploymentId}</TextLink>
          ) : (
            <i>no deployment</i>
          )}{" "}
          {d.deploymentIsDeleted && (
            <Label size="mini" color="grey">
              deleted
            </Label>
          )}{" "}
          <span style={{ color: "#8b958f" }}>{_.compact([d.deploymentName && d.deploymentId, d.model, d.region]).join(" · ")}</span>
        </Table.Cell>
        <NumCell />
        {COMPONENTS.map((c) => (
          <NumCell key={c.key}>{d.credits[c.key] ? fmt(d.credits[c.key]) : "-"}</NumCell>
        ))}
        <NumCell>{fmt(d.total)}</NumCell>
        <NumCell colSpan={2} />
      </DeploymentRow>
    ));
  };

  return (
    <ContentSegment>
      <SecondaryMenu>
        <Menu.Item header>Usage</Menu.Item>
      </SecondaryMenu>
      <Menu borderless pointing stackable>
        <Menu.Item>
          <Dropdown
            selection
            value={presetKey}
            options={[
              ...presets.map((p) => ({ key: p.key, value: p.key, text: p.text })),
              { key: "custom", value: "custom", text: "Custom range", disabled: true },
            ]}
            onChange={(_e, d) => onPresetChanged(d.value as string)}
          />
        </Menu.Item>
        <Menu.Item>
          <DateRangePicker
            small
            startDate={pickerStart}
            startDateId="usageOverviewStartDate"
            endDate={pickerEnd}
            endDateId="usageOverviewEndDate"
            displayFormat="YYYY-MM-DD"
            firstDayOfWeek={1}
            minimumNights={0}
            numberOfMonths={2}
            showClearDates={false}
            // No usage exists in the future; anything in the past is fair game.
            isOutsideRange={(day: moment.Moment) => calendarDay(day) > utcToday()}
            initialVisibleMonth={() => from.clone()}
            focusedInput={focusedInput}
            onFocusChange={(f) => setFocusedInput(f)}
            onDatesChange={onDatesChanged}
          />
        </Menu.Item>
        <Menu.Item>
          <Input icon="search" placeholder="Org Name or ID" value={search} onChange={(_e, d) => setSearch(d.value)} />
        </Menu.Item>
        <Menu.Item>
          <Dropdown
            selection
            value={tier}
            options={[{ key: "all", text: "Tier: all", value: "all" }, ...tierOptions.map((t) => ({ key: t, text: `Tier: ${t}`, value: t }))]}
            onChange={(_e, d) => setTier(d.value as string)}
          />
        </Menu.Item>
        <Menu.Item>
          <Checkbox toggle label="Hide orgs without credits" checked={hideZero} onChange={() => setHideZero(!hideZero)} />
        </Menu.Item>
        <Menu.Item position="right">
          <Button basic size="small" icon="download" content="Export CSV" disabled={loading || orgs.length === 0} onClick={onExportCSV} />
        </Menu.Item>
      </Menu>

      {rangeTooLong && (
        <Message warning>
          The selected period is {rangeDays} days long. Select a period of at most {MAX_RANGE_DAYS} days.
        </Message>
      )}
      {error && (
        <Message negative>
          <Message.Header>Failed to load usage</Message.Header>
          {error}
        </Message>
      )}

      <Dimmer.Dimmable dimmed={loading}>
        <Dimmer inverted active={loading}>
          <Loader inverted active={loading}>
            Aggregating usage of all organizations&hellip;
          </Loader>
        </Dimmer>

        <SummaryCard>
          <div className="cell left">
            <div className="title">
              <span>{formatRange(from, to)}</span>
              <span className="ctx">
                {rangeDays} day{rangeDays === 1 ? "" : "s"} (UTC)
                {inProgress ? ", in progress" : ""}
              </span>
            </div>
            <div className="kpis">
              <div className="kpi kpi--total">
                <span className="kpi__value">{fmt(grandTotal)}</span>
                <span className="kpi__label">Credits</span>
              </div>
              <div className="kpi">
                <span className={`kpi__value ${deltaPct === undefined ? "" : deltaPct >= 0 ? "up" : "down"}`}>
                  {deltaPct === undefined ? "-" : fmtPct(deltaPct)}
                </span>
                <Popup
                  size="mini"
                  content={`Compared with the ${rangeDays} days before (${formatRange(prevFrom, prevTo)})${
                    inProgress ? ". The current period is still in progress, so it is not complete yet." : ""
                  }`}
                  on={["hover", "focus"]}
                  trigger={
                    <span className="kpi__label" tabIndex={0}>
                      vs prev. period
                    </span>
                  }
                />
              </div>
              <div className="kpi">
                <span className="kpi__value">{orgs.length}</span>
                <span className="kpi__label">Organizations</span>
              </div>
              <div className="kpi">
                <span className="kpi__value">{deploymentCount}</span>
                <span className="kpi__label">Deployments</span>
              </div>
            </div>
          </div>
          <div className="cell right">
            <div className="title">
              <span>By component</span>
              <Popup
                size="mini"
                content="Billing 2.0 usage items starting in the period, excluding non-billable tiers. Includes items that are not invoiced yet; items that are not priced yet count as 0."
                on={["hover", "focus"]}
                trigger={
                  <span className="ctx" tabIndex={0}>
                    credits, incl. not-yet-invoiced items
                  </span>
                }
              />
            </div>
            <div className="bar">
              {COMPONENTS.map((c) =>
                totals[c.key] > 0 ? (
                  <Popup
                    key={c.key}
                    size="mini"
                    content={`${c.name}: ${fmt(totals[c.key])} credits`}
                    trigger={<div style={{ width: `${(totals[c.key] / (grandTotal || 1)) * 100}%`, background: c.color }} />}
                  />
                ) : null
              )}
            </div>
            <div className="legend">
              {COMPONENTS.map((c) => (
                <span key={c.key}>
                  <span className="sw" style={{ background: c.color }} />
                  {c.name} <b>{fmt(totals[c.key])}</b> ({grandTotal ? ((totals[c.key] / grandTotal) * 100).toFixed(1) : "0.0"}%)
                </span>
              ))}
            </div>
          </div>
        </SummaryCard>

        <Table sortable striped compact celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell collapsing />
              <Table.HeaderCell {...sortProps("name")}>Organization</Table.HeaderCell>
              <Table.HeaderCell {...sortProps("tier")}>Tier</Table.HeaderCell>
              <Table.HeaderCell {...sortProps("deployments")} textAlign="right">
                Depl.
              </Table.HeaderCell>
              {COMPONENTS.map((c) => (
                // The header itself is the (focusable) trigger, so the kinds also show on keyboard focus.
                <Popup
                  key={c.key}
                  size="mini"
                  content={c.kinds.join(", ")}
                  on={["hover", "focus"]}
                  trigger={
                    <Table.HeaderCell {...sortProps(c.key)} textAlign="right">
                      {c.name}
                    </Table.HeaderCell>
                  }
                />
              ))}
              <Table.HeaderCell {...sortProps("total")} textAlign="right">
                Total
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Share</Table.HeaderCell>
              <Table.HeaderCell {...sortProps("delta")} textAlign="right">
                vs prev. period
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {orgs.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={COMPONENTS.length + 7}>
                  {loading ? "Loading…" : allOrgs.length === 0 ? "No usage in this period." : "No organizations match the filters."}
                </Table.Cell>
              </Table.Row>
            )}
            {orgs.map((o) => {
              const share = grandTotal ? (o.total / grandTotal) * 100 : 0;
              const isOpen = expanded.has(o.organizationId);
              return (
                <React.Fragment key={o.organizationId}>
                  <Table.Row>
                    <Table.Cell collapsing>
                      <Icon
                        link
                        name={isOpen ? "caret down" : "caret right"}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? "Hide" : "Show"} deployments of ${o.organizationName || o.organizationId}`}
                        onClick={() => toggle(o.organizationId)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggle(o.organizationId);
                          }
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <TextLink href={Routes.dashboard_sales_organization_detailsWithId(o.organizationId)}>{o.organizationName || o.organizationId}</TextLink>
                      {o.organizationIsDeleted && (
                        <Label size="mini" color="grey" style={{ marginLeft: 6 }}>
                          deleted
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>{o.tier}</Table.Cell>
                    <NumCell>{o.deploymentCount}</NumCell>
                    {COMPONENTS.map((c) => (
                      <NumCell key={c.key}>{o.credits[c.key] ? fmt(o.credits[c.key]) : "-"}</NumCell>
                    ))}
                    <NumCell>
                      <b>{fmt(o.total)}</b>
                    </NumCell>
                    <NumCell>
                      {share.toFixed(1)}%
                      <ShareBar>
                        <div style={{ width: `${Math.min(100, share)}%` }} />
                      </ShareBar>
                    </NumCell>
                    {deltaCell(o.total, o.previousTotal)}
                  </Table.Row>
                  {isOpen && deploymentRows(o)}
                </React.Fragment>
              );
            })}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell colSpan={2}>
                <b>Total</b> ({orgs.length} orgs)
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{deploymentCount}</Table.HeaderCell>
              {COMPONENTS.map((c) => (
                <Table.HeaderCell key={c.key} textAlign="right">
                  {fmt(totals[c.key])}
                </Table.HeaderCell>
              ))}
              <Table.HeaderCell textAlign="right">
                <b>{fmt(grandTotal)}</b>
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{grandTotal ? "100%" : "-"}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{deltaPct === undefined ? "-" : fmtPct(deltaPct)}</Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Dimmer.Dimmable>
    </ContentSegment>
  );
};

export default UsageOverview;
